import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { pdf } from "@react-pdf/renderer";
import { useParams } from "react-router-dom";
import OrderPdf from "./OrderPdf";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { SERVER_BASE_URL } from "../Config";
import { formatStatus, AVAILABLE_STATUSES, formatDeliveryMethod } from "../utils/statusUtils";
import { SelectChangeEvent } from "@mui/material/Select";
import { ProductInfo } from "../AllProducts";

interface Order {
  provider?: Pharmacist | null;
  doctor?: Doctor | null;
  specialInstructions: string;
  fulfillment_address: string;  // Changed to match backend snake_case
  deliveryMethod: string;
  billingOption: string;
  patientAddress?: Address;
  providerAddress?: Address;
  strength: string;
  quantity: number;
  products: { product: ProductInfo }[];
  patient: PatientInformation;
  status: string;
  updates: StatusUpdate[];
  needBy: string;
  isAsap: boolean;
  created: string;
}

interface ProductOrder {
  directions: string;
  quantity: number;
  strength: string;
  duration: string;
  price: number;
  provider_price: number | null;
  days_of_supply: number;
  refills: number;
}

interface Address {
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface PatientInformation {
  addresses: any;
  email: string;
  name: string;
  dob: string;
  phone: string;
}

interface Pharmacist {
  id: number;
  name: string;
  email: string;
  phone: string;
  npi: number | null;
  practiceName?: string;
}

interface Doctor {
  id: number;
  name: string;
  npi: number;
  phone?: string;
}

interface StatusUpdate {
  created: string;
  pharmacist: Pharmacist;
  oldStatus: string;
  newStatus: string;
  note: string;
}

const formatDateOnly = (dateString: string) => {
  if (!dateString) return "";
  // Split the date string and create a date in local timezone to avoid off-by-one errors
  const [year, month, day] = dateString.split('T')[0].split('-');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
};

const ManagePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  let { id } = useParams();
  const [order, setOrder] = useState<Order>();
  const [productOrder, setProductOrder] = useState<ProductOrder>();
  const [product, setProduct] = useState<ProductInfo>();
  const [address, setAddress] = useState<Address>();
  const [patientInfo, setPatientInfo] = useState<PatientInformation>();
  const [newStatus, setNewStatus] = useState<string>("");
  const [note, setNote] = useState<string>("");

  const formatBillingOption = (option: string) => {
    switch (option) {
      case 'patientPays': return 'Patient Pays';
      case 'doctorPays': return 'Doctor Pays';
      default: return option || 'N/A';
    }
  };

  const handleExportPdf = async () => {
    if (!order || !product || !patientInfo || !productOrder) return;
    const blob = await pdf(
      <OrderPdf order={order} product={product} productOrder={productOrder} patientInfo={patientInfo} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `order-${id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadOrderInfo = async () => {
    const pharmacistToken = localStorage.getItem("pharmacistToken");
    const response = await fetch(`${SERVER_BASE_URL}/manage_orders/${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${pharmacistToken}`,
      },
    });
    const data = await response.json();
    setOrder(data);
    setProductOrder(data.products[0]);
    setProduct(data.products[0].product);
    setAddress(data.address);
    setPatientInfo(data.patient);
    setNewStatus(data.status);
  };

  useEffect(() => {
    loadOrderInfo();
  }, []);

  const handleOrderStatusChange = (event: SelectChangeEvent<string>) => {
    setNewStatus(event.target.value);
  };

  const onSubmit = () => {
    const body = {
      note: note,
      newStatus: newStatus,
    };
    const pharmacistToken = localStorage.getItem("pharmacistToken");
    fetch(`${SERVER_BASE_URL}/manage_orders/${id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${pharmacistToken}`,
      },
      body: JSON.stringify(body),
    }).then((response) => {
      if (response.ok) {
        loadOrderInfo();
        setNote(""); // Clear notes after successful update
      }
    });
  };

  if (!order || !product || !patientInfo || !productOrder) {
    return <div>Loading...</div>;
  }

  const renderStatusChip = (status: string) => {
    const label = formatStatus(status);
    const chipStyles: Record<string, { backgroundColor: string; color: string }> = {
      PENDING: { backgroundColor: "#ed6c02", color: "#fff" },
      DELIVERED: { backgroundColor: "#2e7d32", color: "#fff" },
      PROCESSING_EXCEPTION: { backgroundColor: "#1976d2", color: "#fff" },
      CANCELLED: { backgroundColor: "#d32f2f", color: "#fff" },
      CANCELLATION_REQUESTED: { backgroundColor: "#fff", color: "#d32f2f" },
    };
    const style = chipStyles[status] || {};
    return <Chip label={label} size="medium" sx={{ ...style, fontSize: "0.95rem", mt: 0.5, border: status === "CANCELLATION_REQUESTED" ? "1px solid #d32f2f" : undefined }} />;
  };

  const renderInfoRow = (label: string, value: string | React.ReactNode) => (
    <Box sx={{ my: 1, textAlign: "left" }}>
      <Typography variant="body2" color="text.secondary" component="div"><strong>{label}:</strong></Typography>
      <Typography variant="body1" component="div">{value}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1 }}>
        <Button variant="contained" color="primary" href="/pharmacy" size={isMobile ? "small" : "medium"}>
          <ArrowBackIcon sx={{ mr: 0.5 }} /> {isMobile ? "Back" : "Return to Orders"}
        </Button>
        <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf} size={isMobile ? "small" : "medium"}>
          {isMobile ? "PDF" : "Export to PDF"}
        </Button>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ mb: 2, p: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Prescription Drug</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    width: { xs: 100, sm: 150 },
                    height: { xs: 100, sm: 150 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    overflow: 'hidden',
                    mx: { xs: "auto", sm: 0 },
                  }}
                >
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={8}>
                {renderInfoRow("Name", product.name)}
                {renderInfoRow("Type", product.type)}
                {renderInfoRow("Dosage Form", product.dosageForm)}
                {renderInfoRow("Strength", productOrder.strength)}
                {renderInfoRow("Size", productOrder.duration)}
                {renderInfoRow("Quantity", productOrder.quantity.toString())}
                {renderInfoRow("Directions", productOrder.directions)}
                {renderInfoRow("Days of Supply", productOrder.days_of_supply.toString())}
                {renderInfoRow("Refills", productOrder.refills.toString())}
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ mb: 2, p: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Patient Information</Typography>
            {renderInfoRow("Name", patientInfo.name)}
            {renderInfoRow("Email", patientInfo.email)}
            {renderInfoRow("Phone", patientInfo.phone)}
            {renderInfoRow("Date of Birth", formatDateOnly(patientInfo.dob))}
            {renderInfoRow("Addresses", (
              <>
                {patientInfo.addresses.map((address: any, index: any) => (
                  <div key={index}>
                    {address.street}, {address.city}, {address.state} {address.zip}
                  </div>
                ))}
              </>
            ))}
          </Paper>

          <Paper sx={{ mb: 2, p: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Provider Information</Typography>
            {renderInfoRow("Name", order.provider?.name || "N/A")}
            {renderInfoRow("Email", order.provider?.email || "N/A")}
            {renderInfoRow("Phone", order.provider?.phone || "N/A")}
            {renderInfoRow("NPI", order.provider?.npi ? order.provider.npi.toString() : "N/A")}
            {renderInfoRow("Clinic Name", order.provider?.practiceName || "N/A")}
          </Paper>

          <Paper sx={{ mb: 2, p: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Fulfillment and Delivery</Typography>
            {renderInfoRow("Delivery Method", formatDeliveryMethod(order.deliveryMethod))}
            {renderInfoRow("Fulfillment Address", order.fulfillment_address || "N/A")}
            {order.isAsap ? (
              renderInfoRow("Priority", "ASAP")
            ) : (
              renderInfoRow("Need By Date", order.needBy ? new Date(order.needBy.includes('T') ? order.needBy : order.needBy + 'T12:00:00').toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              }) : "N/A")
            )}
            {renderInfoRow("Billing Option", formatBillingOption(order.billingOption))}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Order Special Instructions</Typography>
            <Typography variant="body1" sx={{ textAlign: "left", whiteSpace: "pre-line" }}>{order.specialInstructions || "N/A"}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"}>Order Summary</Typography>
            {renderInfoRow("Order Number", `#${id}`)}
            {renderInfoRow("Retail Price", `$${(productOrder.price / 100 * productOrder.quantity).toFixed(2)}`)}
            {productOrder.provider_price != null && renderInfoRow("Provider Price", `$${(productOrder.provider_price / 100 * productOrder.quantity).toFixed(2)}`)}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Update Order Status</Typography>
            {renderInfoRow("Current Status", renderStatusChip(order.status))}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                onChange={handleOrderStatusChange}
                label="Status"
              >
                {AVAILABLE_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>{formatStatus(status)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes..."
              value={note}
              onChange={(event) => setNote(event.target.value)}
              multiline
              rows={4}
              fullWidth
              sx={{ mt: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              sx={{ width: "100%", borderRadius: "20px", mt: 2 }}
              onClick={onSubmit}
            >
              Save
            </Button>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant={isMobile ? "h6" : "h5"} mb={2}>Task History</Typography>
            {order.updates && order.updates.length > 0 && (
              order.updates.slice().reverse().map((update, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(update.created).toLocaleString()}
                  </Typography>
                  <Typography variant="body1">
                    Status changed from <strong>{formatStatus(update.oldStatus)}</strong> to <strong>{formatStatus(update.newStatus)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    By: {update.pharmacist ? update.pharmacist.name : (order.provider?.name || "Provider")}
                  </Typography>
                  {update.note && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Note: {update.note}
                    </Typography>
                  )}
                </Box>
              ))
            )}
            {/* Always show the initial order creation */}
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {new Date(order.created).toLocaleString()}
              </Typography>
              <Typography variant="body1">
                Order created with status <strong>{formatStatus("PENDING")}</strong>
              </Typography>
              <Typography variant="body2">
                By: {order.provider?.name || "Provider"}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ManagePage;
